import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import NavMarker from 'react-rhumb/NavMarker';
import Raven from 'raven-js';
import { parse as parseParams } from 'hub-http/helpers/params';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage'; // @ts-expect-error untyped dependency

import FormattedDuration from 'I18n/components/FormattedDuration';
import Loadable from 'UIComponents/decorators/Loadable';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import * as ReviewType from 'marketplace-ui-reviews/constants/ReviewType';
import Review from 'marketplace-ui-reviews/records/Review';
import getDisplayedReviewerName from 'marketplace-ui-reviews/utils/getDisplayedReviewerName';
import { getUserEmail, getUserFirstName, getUserLastName // @ts-expect-error untyped dependency
} from '../../../selectors/auth'; // @ts-expect-error untyped dependency

import { submitReview } from '../../../actions/ReviewsActions'; // @ts-expect-error untyped dependency

import { postNotification as postUnreviewableAlert } from 'ReduxMessenger/actions/NotificationActions';
import { UNREVIEWABLE_ALERT_ID, UnreviewableReasons // @ts-expect-error untyped dependency
} from '../../../constants/Reviewability'; // @ts-expect-error untyped dependency

import { getReviewWriteRequestState } from '../../../selectors/requestStates';
const AsyncReviewWizard = Loadable({
  loader: () => import(
  /* webpackChunkName: 'review-wizard' */
  'marketplace-ui-reviews/components/wizard/AppReviewWizard')
});
export default function ReviewWizard({
  appName,
  appIcon,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  reviewability,
  slug,
  supportLink
}) {
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const reviewWriteStatus = useSelector(getReviewWriteRequestState);
  const userEmail = useSelector(getUserEmail);
  const dispatch = useDispatch();
  const displayName = getDisplayedReviewerName({
    firstName,
    lastName
  });
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const overallRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const defaultReview = useMemo(() => {
    let storedReview = null;

    try {
      storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
    } catch (__error) {// pass through
    }

    let displayedReview = storedReview ? new Review(storedReview) : new Review();

    if (overallRating) {
      displayedReview = displayedReview.setOverallRating(overallRating);
    }

    return displayedReview;
  }, [overallRating, slug]);
  const handleSubmit = useCallback(review => new Promise((resolve, reject) => {
    sessionStorage.removeItem(`${slug}_review`);
    onTrackInteraction('submit review', 'review', undefined, {
      reviewSource
    });
    return dispatch(submitReview(review.set('createdAt', new Date().valueOf()).set('reviewerDisplayName', displayName).set('entityId', offeringId).set('reviewType', ReviewType.APP), slug)).then(() => {
      resolve('Successfully submitted review');
      onSuccess();
    }).catch(err => {
      Raven.captureMessage('Failed to leave a review', {
        level: 'error',
        extra: Object.assign({
          offeringId
        }, err)
      });
      reject(err);
    });
  }), [dispatch, displayName, offeringId, onSuccess, onTrackInteraction, slug, reviewSource]);
  const updateSessionStorage = useCallback(updatedReview => {
    sessionStorage.setItem(`${slug}_review`, JSON.stringify(updatedReview));
  }, [slug]);
  const onWizardLoad = useCallback(() => {
    onTrackInteraction('open review wizard', 'review', undefined, {
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onStepChange = useCallback((prevStep, nextStep) => {
    onTrackInteraction('change review wizard step', 'review', nextStep, {
      prevStep,
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onReject = useCallback(() => {
    onTrackInteraction('dismiss review wizard', 'review', undefined, {
      reviewSource
    });
    onClose();
  }, [onTrackInteraction, onClose, reviewSource]);
  useEffect(() => {
    if (!reviewability.isReviewable()) {
      const reason = reviewability.getUnreviewableReason();

      let message = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `appMarketplace.detail.reviews.unreviewable.${reason}`
      });

      if (reason === UnreviewableReasons.UNINSTALLED) {
        const uninstallWindow = /*#__PURE__*/_jsx(FormattedDuration, {
          from: 1,
          to: reviewability.get('maxMillisAfterUninstall')
        });

        message = /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: `appMarketplace.detail.reviews.unreviewable.${reason}`,
          options: {
            uninstallWindow
          }
        });
      }

      dispatch(postUnreviewableAlert({
        id: UNREVIEWABLE_ALERT_ID,
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.unreviewable.titleText"
        }),
        message
      }));
    }
  }, [dispatch, reviewability]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: reviewability.isReviewable() && /*#__PURE__*/_jsx(AsyncReviewWizard, {
      reviewableName: appName,
      reviewableIconUrl: appIcon,
      failed: reviewWriteStatus === RequestState.FAILED,
      defaultReview: defaultReview,
      displayName: displayName,
      loading: reviewWriteStatus === RequestState.PENDING,
      onReject: onReject,
      onChange: updateSessionStorage,
      onConfirm: handleSubmit,
      onStepChange: onStepChange,
      onWizardLoad: onWizardLoad,
      supportLink: supportLink,
      userEmail: userEmail
    })
  });
}